import { Divider, Grid, Button, CircularProgress } from "components/Mui";
import React, { useEffect, useContext, useRef, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import AccrualRules from "./AccrualRules/AccrualRules";
import Rollover from "./Rollover/Rollover";
import Leaves from "./Leaves/Leaves.";

import { getErrorMessage, getTenantId, isObjectEmpty } from "utility/helpers/utils";
import { getPolicy } from "utility/apis/SetupGuideService";
import { ProfileContext } from "containers/ProfileDetails/ProfileDetails";
import Loading from "components/Loading/Loading";

import "./TimeOffPolicyEmployeeForm.scss";

const TimeOffPolicyEmployeeForm = (props) => {
  const { dataFromServer, setSaveTimeOffData = () => {}, handleSave, withSaveButton, employeeId, profileCustomizeTimeOff, setProfileCustomizeTimeOff,  currentTabIndex, setCurrentTabIndex, backClicked, showResetToDefault } = props;
  const profileContext = useContext(ProfileContext);
  const hasRendered = useRef(false);

  const { register, control, handleSubmit, reset, errors, getValues, trigger } = useForm({
    defaultValues: {
      ...profileCustomizeTimeOff,
      increaseSet: profileCustomizeTimeOff?.increaseSet || dataFromServer?.increaseSet || [],
    },
  });
  
  const { fields, append, remove } = useFieldArray({
    control,
    name: "increaseSet",
  });

  useEffect(() => {
    if (hasRendered.current && !withSaveButton) {
      // Code to run after the first render
      setCurrentTabIndex(currentTabIndex);
      handleProfileCustomizeTimeOffBackStep()
    } else {
      hasRendered.current = true;
    }
  }, [backClicked])


  const handleProfileCustomizeTimeOffSaveStep = async (event) => {
    const {saveProfile, setSaveProfile} = profileContext;
    event && event.preventDefault();
    try {
      const isValid = await trigger();
      if (isValid) {
        // Handle successful validation
        setProfileCustomizeTimeOff(getValues())
        setSaveProfile(!saveProfile)
      } 
    } catch(e){

    }
   
  }

  const handleProfileCustomizeTimeOffBackStep = () => {
    try {
        setProfileCustomizeTimeOff(getValues())
        setCurrentTabIndex(currentTabIndex - 1);
    } catch(e){

    }
   
  }

  useEffect(() => {
    if (dataFromServer || profileCustomizeTimeOff) {
      reset(profileCustomizeTimeOff || dataFromServer || {});
    }
  }, [dataFromServer, profileCustomizeTimeOff, reset]);

  const tenantId = getTenantId(useLocation(), "tenantId");
  const [isResetting, setIsResetting] = useState(false);

  // Mirrors "Reset to default" on the WFH customization tab. The entity's default time-off policy
  // is saved through the normal save path: the backend recognises values equal to the entity
  // default, points the employee back at the default and deletes their custom policy when no one
  // else uses it (TimeOffPolicyServiceImp.updateTimeOffPolicyForEmployee). When creating a profile
  // there is nothing to save yet, so the form is only refilled.
  const resetToDefault = async () => {
    if (isResetting) return;
    setIsResetting(true);
    try {
      const response = await getPolicy(tenantId);
      const defaults = response?.data?.timeOffPolicy;
      if (!defaults) {
        toast.error("This entity has no default time off policy");
        return;
      }
      if (employeeId) {
        // A copy: the save handler writes ids into increaseSet.
        await handleSave({ ...defaults, increaseSet: (defaults.increaseSet || []).map((rule) => ({ ...rule })) });
      }
      reset(defaults);
      setSaveTimeOffData(defaults);
      setProfileCustomizeTimeOff && setProfileCustomizeTimeOff(defaults);
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setIsResetting(false);
    }
  };

  const handleChangedForm = () =>{
    setTimeout(() => {
      const currentValues = { ...getValues() };

      setSaveTimeOffData(currentValues);
      setProfileCustomizeTimeOff && setProfileCustomizeTimeOff(currentValues);
    }, 0);
  }

  return (
    <>
      {isObjectEmpty(dataFromServer) && profileCustomizeTimeOff === undefined? (
        <Loading></Loading>
      ) : (
        <div id="WfhMonthlyBasis" className="c-time-off-policy-cont ps-4">
          <form id="time-off-policy" onChange={handleChangedForm} onClick={handleChangedForm} onBlur={handleChangedForm} onSubmit={(employeeId || withSaveButton) ? handleSubmit(handleSave) : (event) => handleProfileCustomizeTimeOffSaveStep(event)}>
            <Grid container spacing={{ md: 1, xs: 0 }}>
              {fields.length > 0 ? (
                  <AccrualRules
                      register={register}
                      fields={fields}
                      control={control}
                      append={append}
                      remove={remove}
                      errors={errors}
                      handleChangedForm={handleChangedForm}
                  />
              ) : (
                  <div className="loading-container">
                    <Loading />
                  </div>
              )}
              <Grid item xs={12} spacing={3}>
                <Divider
                  sx={{ bgcolor: "black", p: 0, mt: 3 }}
                  className="form-divider"
                />
              </Grid>
              <Rollover
                errors={errors}
                register={register}
                control={control}
                handleChangedForm={handleChangedForm}
                dataFromServer={dataFromServer && Object.keys(dataFromServer).length > 0 ? dataFromServer : profileCustomizeTimeOff}
              />
              <Grid item xs={12} spacing={3}>
                <Divider
                  sx={{ bgcolor: "black", p: 0 }}
                  className="form-divider"
                />
              </Grid>
              <Leaves
                errors={errors}
                register={register}
                control={control}
                dataFromServer={dataFromServer && Object.keys(dataFromServer).length > 0 ? dataFromServer : profileCustomizeTimeOff}
              />
            </Grid>
            {withSaveButton &&
                <Button type="submit" variant="contained" className='float-end my-3' >
                  Save
                </Button>
            }
            {/* Only an employee's customization can go back to the entity default; on the entity's
                own Time Off Policies page the form already is the default. */}
            {showResetToDefault &&
                <Button
                    type="button"
                    className="float-start my-3 bg-danger" color="secondary" variant="outlined"
                    onClick={resetToDefault}
                >
                  {isResetting ? <CircularProgress size={24} color="inherit" /> : "Reset to default"}
                </Button>
            }
          </form>
        </div>
      )}
    </>
  );
};

export default TimeOffPolicyEmployeeForm;
